// /host — Manifesto + pledge. No inline form (that's /apply's job).
// One narrative, one CTA → /apply. First-person plural. Founder-host voice.
//
// We keep `/partner` aliased to this same component via App.js routing so old
// links still resolve, but `/host` is the canonical URL going forward.
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Mountain, ArrowLeft, Check, ArrowRight, MessageCircle,
  Sparkles, Heart, Users, Calendar,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };
const ACCENT = '#3a5a40';

const HostLanding = () => (
  <div className="min-h-screen bg-[#faf8f4]" data-testid="host-landing">
    <SEOHead
      title="Host with GoHimalayan — Free, Forever"
      description="Independent host of an experiential Himalayan stay? Join GoHimalayan's editorial discovery platform. Zero commission. Zero listing fee. Forever."
      url="/host"
    />
    <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap" rel="stylesheet" />

    {/* ── Sticky nav ── */}
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#faf8f4]/90 backdrop-blur-md border-b border-stone-200/40">
      <div className="max-w-6xl mx-auto px-6 md:px-12 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Mountain className="h-6 w-6" style={{ color: ACCENT }} strokeWidth={2.5} />
          <span className="text-lg font-bold tracking-tight text-stone-900" style={pf}>GoHimalayan</span>
        </Link>
        <Link to="/" className="flex items-center gap-2 text-stone-500 hover:text-stone-800 transition-colors">
          <ArrowLeft className="w-4 h-4" /><span className="text-xs tracking-wider uppercase">Home</span>
        </Link>
      </div>
    </nav>

    {/* ── Hero — the offer ── */}
    <section className="pt-28 pb-12 px-6 md:px-12" data-testid="host-hero">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-[11px] tracking-[0.4em] uppercase font-semibold mb-6" style={{ color: ACCENT }}>For independent Himalayan hosts</p>
        <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold text-stone-900 leading-[1.1] tracking-tight mb-8" style={pf}>
          Your story belongs in front of the world.
          <br />
          <span className="italic" style={{ color: ACCENT }}>Not buried on page seventeen.</span>
        </h1>
        <p className="text-stone-600 text-lg md:text-xl max-w-2xl mx-auto leading-[1.7] mb-10" style={pf}>
          GoHimalayan is built by hosts for hosts. We feature, write about, and connect travellers directly to experiential stays across the Himalayas. <strong className="text-stone-900">Free. Forever. No commission.</strong>
        </p>

        {/* The big promise — three numbers */}
        <div className="grid grid-cols-3 gap-3 max-w-xl mx-auto mb-10">
          {[
            { v: '0%', l: 'commission' },
            { v: '₹0', l: 'listing fee' },
            { v: '∞', l: 'forever' },
          ].map((m, i) => (
            <div key={i} className="bg-white border border-stone-200 rounded-xl py-5">
              <div className="text-3xl sm:text-4xl font-bold text-stone-900" style={pf}>{m.v}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-stone-400 mt-1">{m.l}</div>
            </div>
          ))}
        </div>
        <Link
          to="/apply"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-white transition"
          style={{ background: ACCENT }}
          data-testid="host-hero-apply"
        >Tell us about your stay<ArrowRight className="w-4 h-4" /></Link>
        <p className="text-[12px] text-stone-400 mt-4">No commitment. We'll look at your property and reply within 48 hours.</p>
      </div>
    </section>

    {/* ── Why we're doing this (the founder confession) ── */}
    <section className="py-16 px-6 md:px-12" data-testid="host-why">
      <div className="max-w-3xl mx-auto">
        <div className="w-12 h-[1px] bg-stone-300 mx-auto mb-12" />
        <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 text-center mb-3 font-semibold">A note from the founders</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 text-center mb-10" style={pf}>
          We built a homestay first. Then we built this.
        </h2>
        <div className="space-y-6 text-stone-600 text-base md:text-[17px] leading-[1.9]" style={pf}>
          <p>
            GoHimalayan started in a riverside cottage in the Tirthan Valley — <em>Bluehouse by the River</em>. We poured years into making it feel like home. And then we watched OTAs swallow 18% of every booking, demand discounts, and rank us by price. The story we'd worked so hard to build didn't matter.
          </p>
          <p>
            Worse, our guests would WhatsApp us before booking. They wanted to <em>talk</em> first — about the food, the dog, whether the river was running high. The platforms made that nearly impossible.
          </p>
          <p>
            So we decided to build the thing we wished existed. An editorial home for independent Himalayan hosts. Where the story comes first, the host gets the booking direct, and no platform takes a cut.
          </p>
          <p className="text-stone-900 font-medium">
            That's GoHimalayan. We're hosts ourselves. We know what hurts. We're not solving for scale — we're solving for fairness.
          </p>
        </div>
      </div>
    </section>

    {/* ── What you get ── */}
    <section className="py-20 px-6 md:px-12 bg-white border-y border-stone-100" data-testid="host-what">
      <div className="max-w-5xl mx-auto">
        <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 text-center mb-3 font-semibold">What being on GoHimalayan looks like</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 text-center mb-14" style={pf}>
          Everything we do for you. None of it costs you a rupee.
        </h2>

        <div className="grid md:grid-cols-2 gap-x-12 gap-y-8 max-w-3xl mx-auto">
          {[
            { icon: Sparkles, t: 'Editorial story', d: "Our team writes your property's storyline — the way a travel magazine would profile you." },
            { icon: Calendar, t: "A page that's found", d: 'Your own SEO-optimised property page on gohimalayan.com, indexed by Google, surfaced in search.' },
            { icon: MessageCircle, t: 'Direct WhatsApp bookings', d: "Enquiries arrive on your phone with the guest's name, dates, and party size. You take it from there." },
            { icon: Heart, t: 'Curated imagery', d: 'We help select the photos that show your property at its best. No staged shoots required — we work with what you have.' },
            { icon: Users, t: 'Community reach', d: 'Yoga, cycling, art and corporate retreat enquiries channelled directly to relevant hosts as the platform grows.' },
            { icon: Check, t: 'A platform of peers', d: "Every property here is hand-picked. You're in the company of stays that share your values, not your discount level." },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-4" data-testid={`host-benefit-${i}`}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${ACCENT}15` }}>
                <f.icon className="w-5 h-5" style={{ color: ACCENT }} strokeWidth={1.75} />
              </div>
              <div>
                <p className="text-stone-900 text-base font-semibold mb-1" style={pf}>{f.t}</p>
                <p className="text-stone-500 text-[14px] leading-relaxed">{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Hosts in good company ── */}
    <section className="py-20 px-6 md:px-12" data-testid="host-peers">
      <div className="max-w-3xl mx-auto">
        <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 text-center mb-3 font-semibold">A platform of peers</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 text-center mb-10" style={pf}>
          Who you'll share the platform with.
        </h2>
        <div className="space-y-6 text-stone-600 text-base md:text-[17px] leading-[1.9]" style={pf}>
          <p>
            GoHimalayan is hand-picked. No resorts. No chains. No listings that don't feel built by a person who actually lives in the mountains.
          </p>
          <p>
            You'll be alongside a riverside cottage in Tirthan, a heritage manor in Lahaul, a cedar lodge in Sikkim, a tea estate retreat in Palampur. Every property different. Every host with a name. Every story singular.
          </p>
          <p>
            As the platform grows, the opportunities grow with it — for everyone on it. Community group bookings, corporate retreats, valley tourism partnerships, off-season campaigns. <strong className="text-stone-900">The Himalayan brand of hospitality has global resonance. The world needs to experience it. We're building the door.</strong>
          </p>
        </div>
      </div>
    </section>

    {/* ── The "free forever" pledge — front and centre, framed as a contract ── */}
    <section className="py-16 px-6 md:px-12" data-testid="host-pledge">
      <div className="max-w-3xl mx-auto">
        <div className="rounded-2xl p-10 md:p-12 text-center border-2 shadow-sm" style={{ background: `${ACCENT}06`, borderColor: `${ACCENT}30` }}>
          <p className="text-[11px] tracking-[0.35em] uppercase font-semibold mb-4" style={{ color: ACCENT }}>The GoHimalayan pledge</p>
          <h3 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-6 leading-tight" style={pf}>
            Free. <span className="italic" style={{ color: ACCENT }}>Forever.</span>
          </h3>
          <p className="text-stone-700 text-base md:text-lg leading-[1.85] max-w-xl mx-auto mb-4" style={pf}>
            No commission on bookings. No listing fee. No "premium tier" hidden behind a paywall. Every rupee from every guest goes directly to you.
          </p>
          <p className="text-stone-600 text-sm md:text-base leading-relaxed max-w-xl mx-auto" style={pf}>
            We may, much later, introduce a paid offering for advanced services. If we ever do — the original hosts on this platform are grandfathered into <em>free, forever</em>. That promise is permanent and unconditional.
          </p>
        </div>
      </div>
    </section>

    {/* ── The honest bit — how GoHimalayan ever makes money ── */}
    <section className="py-16 px-6 md:px-12" data-testid="host-revenue">
      <div className="max-w-3xl mx-auto">
        <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 text-center mb-3 font-semibold">The honest bit</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 text-center mb-10" style={pf}>
          "But how does GoHimalayan survive?"
        </h2>
        <div className="space-y-6 text-stone-600 text-base md:text-[17px] leading-[1.9]" style={pf}>
          <p>
            Fair question. The short answer: <em>not from the host</em>. We're investing our own capital to build this platform deliberately, valley by valley. Where revenue can fairly come from, eventually:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-stone-700">
            <li><strong className="text-stone-900">Community & retreat group bookings</strong> — yoga collectives, cycling tours, art residencies looking for trusted Himalayan partners at scale.</li>
            <li><strong className="text-stone-900">Corporate retreat curation</strong> — companies booking offsites through us, paying us a curation fee.</li>
            <li><strong className="text-stone-900">State tourism partnerships</strong> — working with Himalayan state tourism boards to channel travellers responsibly.</li>
            <li><strong className="text-stone-900">A strategic investor</strong>, later, who shares the vision of a Himalayan hospitality network that the OTAs structurally cannot replicate.</li>
          </ul>
          <p className="text-stone-900 font-medium pt-1">
            None of that touches your bookings. None of it ever will.
          </p>
        </div>
      </div>
    </section>

    {/* ── Final CTA — single button → /apply ── */}
    <section className="py-20 px-6 md:px-12 bg-white border-t border-stone-100" data-testid="host-final-cta">
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-3 font-semibold">If this resonates</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-5" style={pf}>
          Tell us about your stay.
        </h2>
        <p className="text-stone-600 text-base md:text-lg leading-[1.85] max-w-lg mx-auto mb-9" style={pf}>
          A real human from our team will reply within 48 hours. We read every application personally — we're hosts ourselves.
        </p>
        <Link
          to="/apply"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-white transition shadow-sm hover:shadow-md"
          style={{ background: ACCENT }}
          data-testid="host-final-apply"
        >
          Start the conversation
          <ArrowRight className="w-4 h-4" />
        </Link>
        <p className="text-[12px] text-stone-400 mt-5">
          No payment. No commitment. Just a conversation.
        </p>
      </div>
    </section>

    {/* ── Closing — community / retreat path ── */}
    <section className="py-16 px-6 md:px-12 bg-[#1a2a1c]" data-testid="host-community">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-amber-200/70 text-xs tracking-[0.3em] uppercase mb-4 font-semibold">Not a host? You might still be a partner.</p>
        <h3 className="text-2xl sm:text-3xl font-light italic text-white leading-relaxed mb-8" style={pf}>
          Communities · retreats · travel collectives —<br />
          we'd love to host you across the Himalayas.
        </h3>
        <a
          href="https://wa.me/919871155963?text=Hi%20—%20we're%20a%20community%2Fretreat%20group%20looking%20to%20partner%20with%20GoHimalayan."
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold bg-white text-stone-900 hover:bg-stone-100 transition"
          data-testid="host-community-wa"
        ><MessageCircle className="w-4 h-4" />Start a conversation</a>
      </div>
    </section>

    {/* ── Footer ── */}
    <footer className="py-8 bg-[#0f1810] text-center">
      <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <Link to="/" className="text-white/40 text-xs tracking-wider uppercase hover:text-white/60">© 2026 GoHimalayan</Link>
        <div className="flex items-center gap-5 text-xs text-white/40">
          <Link to="/about" className="hover:text-white/70">About</Link>
          <Link to="/editorial-policy" className="hover:text-white/70">Editorial policy</Link>
        </div>
      </div>
    </footer>
  </div>
);

export default HostLanding;
